import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Location, NgForOf, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';

import { GridItem } from './model';
import { ImageLoadingComponent } from '../image-loading/image-loading.component';
import { ColorWithBorder } from '@common/color-pipes';
import { Color, ProductMeta, Size } from '@proto/store/api/common/common';
import { WindowDetectService } from '@service/window-detect.service';
import { Links } from '@helper/links';
import { Urls } from '@helper/urls';
import { LinkToUrl } from '@common/link-pipes';
import { UniversalLinkComponent } from '@common/universal-link/universal-link.component';
import { EventTracker } from '../../../analytics/event-tracker.service';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { ColorLinkPipe, SizeLinkPipe } from '@common/product-grid/pipes';

@Component({
  selector: 'app-product-grid',
  templateUrl: './product-grid.component.html',
  styleUrls: ['./product-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImageLoadingComponent,
    NgForOf,
    NgIf,
    NgTemplateOutlet,
    NgStyle,
    ColorWithBorder,
    LinkToUrl,
    UniversalLinkComponent,
    FocusRemoverDirective,
    ColorLinkPipe,
    RouterLink,
    SizeLinkPipe,
  ],
})
export class ProductGridComponent {
  static typeToken: { item: GridItem };

  // services
  readonly router = inject(Router);
  readonly windowService = inject(WindowDetectService);
  readonly eventTracker = inject(EventTracker);
  readonly location = inject(Location);
  // constants
  readonly colorBlockSize = 16;
  readonly mobileColorBlockSize = 12.5;
  readonly mobileSizeBlockSize = 24;
  readonly sizeBlockSize = 28;
  // signal inputs
  readonly gridWidth = input.required<number>();
  readonly imgRatio = input.required<number>();
  readonly fitImage = input(true);
  readonly gapSize = input(10);
  readonly columns = input(3);
  // if width does not set, it will fill the whole available spaces
  readonly width = input(0);
  // computed attributes
  readonly gridColumn = computed(
    () => `repeat(${this.columns()}, minmax(0, ${this.fitImage() ? this.imgWidthCss() : '1fr'}))`
  );
  readonly gridSize = computed(() => {
    if (!this.windowService.isMobileDevice()) {
      return `${this.gridWidth()}px`;
    }
    return `calc(100vw - ${this.gapSize() * 2}px)`;
  });
  readonly imgWidthCss = computed(() => {
    if (this.windowService.isMobileDevice()) {
      return `calc((100vw - ${(this.columns() + 1) * this.gapSize()}px) / ${this.columns()})`;
    }
    return `${this.imgWidth()}px`;
  });
  readonly blockMarginTop = computed(() => (this.windowService.isMobileDevice() ? 8 : this.colorSizeBlockMargin));
  readonly imgWidth = computed(() => {
    const gridWidth = this.windowService.isMobileDevice() ? this.gridWidth() - 2 * this.gapSize() : this.gridWidth();
    return this.width() || Math.floor((gridWidth - (this.columns() - 1) * this.gapSize()) / this.columns());
  });
  readonly imgHeight = computed(() => this.imgWidth() * this.imgRatio());

  @Output() readonly itemClick = new EventEmitter<[GridItem, number]>();

  @Input() rowGapSize: number | undefined;
  @Input() items: GridItem[] = [];
  @Input() metaRef: TemplateRef<any> | null = null;
  @Input() itemStyles: { [key: string]: string }[] | undefined = [];
  @Input() borderRadius = 0;
  @Input() openNewWindow = false;
  @Input() showColor = true;
  @Input() showSize = true;
  @Input() desktopPadding = true;
  @Input() customClick = false;
  @Input() colorSizeBlockMargin = 35;

  openProduct(event: MouseEvent | undefined, link: string, productId?: string, colorId?: string, skuId?: string): void {
    if (!link || event?.ctrlKey || event?.metaKey || event?.shiftKey) {
      return;
    }
    if (this.openNewWindow) {
      return;
    }
    Links.handleLink(
      this.router,
      (skuId ?? productId)
        ? Urls.fromMeta(
            ProductMeta.fromPartial({
              productId: skuId || productId || '',
            }),
            colorId
          )
        : link
    );
  }

  trackByFn(_: number, item: GridItem): string {
    return item.hash;
  }

  trackByColor(_: number, item: Color): string {
    return item.name + item.hex + item.thumbnail;
  }

  trackBySize(_: number, item: Size): string {
    return item.skuId;
  }
}
